/* Store Locator — <store-locator> custom element (sections/store-locator.liquid).

   The server-rendered card list is the single source of truth: each card's
   data-* attributes carry name/address/status/coords, so the page works fully
   without JS. This element adds the Leaflet map (self-hosted assets/leaflet.js,
   pinned 1.9.4, lazy-loaded when the section scrolls into view), a silent
   IP-based proximity sort on load (with precise browser geolocation behind
   the "Use my location" button), and city/ZIP search.

   Markers are inline-SVG divIcons — Shopify's flat assets/ directory can't
   serve leaflet.css's relative marker-image paths, and SVG pins carry brand
   colors anyway. Search geocodes through Nominatim on explicit submit only
   (never keyup), one request at a time, per its usage policy. */

(() => {
  if (customElements.get('store-locator')) return;

  const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
  const EARTH_RADIUS_MILES = 3958.8;
  const PIN_FOREST = '#1d3c34';
  const SEARCH_TIMEOUT_MS = 8000;

  const REDUCED_MOTION = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const SCROLL_BEHAVIOR = REDUCED_MOTION ? 'auto' : 'smooth';

  const toRadians = (degrees) => (degrees * Math.PI) / 180;

  // Same haversine as location-detector.js — duplicated so the section stays
  // self-contained (no load-order dependency on the detector).
  function distanceInMiles(latitudeA, longitudeA, latitudeB, longitudeB) {
    const deltaLatitude = toRadians(latitudeB - latitudeA);
    const deltaLongitude = toRadians(longitudeB - longitudeA);
    const haversine =
      Math.sin(deltaLatitude / 2) ** 2 +
      Math.cos(toRadians(latitudeA)) * Math.cos(toRadians(latitudeB)) * Math.sin(deltaLongitude / 2) ** 2;
    return EARTH_RADIUS_MILES * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  const formatMiles = (miles) => (miles < 10 ? miles.toFixed(1) : String(Math.round(miles)));

  const escapeHtml = (value) => String(value == null ? '' : value)
    .replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.mapEl = this.querySelector('[data-locator-map]');
      this.listEl = this.querySelector('[data-locator-list]');
      this.statusEl = this.querySelector('[data-locator-status]');
      this.searchForm = this.querySelector('[data-locator-search]');
      this.geoButton = this.querySelector('[data-locator-geolocate]');
      this.toggleButton = this.querySelector('[data-locator-toggle]');
      this.recenterButton = this.querySelector('[data-locator-recenter]');
      this.records = this.buildRecords();
      this.listLimit = parseInt(this.dataset.listLimit, 10) || 0;
      this.expanded = false;
      this.map = null;
      this.originMarker = null;
      this.lastOrigin = null;
      this.hasExplicitOrigin = false;

      if (this.searchForm) {
        this.searchForm.addEventListener('submit', this.onSearch.bind(this));
      }
      if (this.toggleButton) {
        this.toggleButton.addEventListener('click', this.onToggleList.bind(this));
      }
      this.initSetLocationButtons();
      if (this.pinCurrentFacility()) {
        this.records.forEach((record) => this.listEl.appendChild(record.card));
      }
      this.applyListLimit();
      this.autoLocate();
      if (this.geoButton && navigator.geolocation && window.isSecureContext) {
        this.geoButton.hidden = false;
        this.geoButton.addEventListener('click', this.onGeolocate.bind(this));
      }

      if (!this.mapEl || !this.records.some((record) => record.lat !== null)) return;
      // CSS hides the map on mobile — follow that truth rather than restating
      // the breakpoint here: load Leaflet only once the container has a box.
      const startWhenVisible = () => {
        if (window.theme && typeof window.theme.initWhenVisible === 'function') {
          window.theme.initWhenVisible({
            element: this,
            callback: this.loadLeaflet.bind(this),
            threshold: 200
          });
        } else {
          this.loadLeaflet();
        }
      };
      if (typeof ResizeObserver === 'undefined') {
        startWhenVisible();
        return;
      }
      const mapSized = new ResizeObserver((entries) => {
        if (!entries.some((entry) => entry.contentRect.width > 0)) return;
        mapSized.disconnect();
        startWhenVisible();
      });
      mapSized.observe(this.mapEl);
    }

    buildRecords() {
      return Array.from(this.querySelectorAll('[data-locator-card]')).map((card) => {
        const lat = parseFloat(card.dataset.lat);
        const lng = parseFloat(card.dataset.lng);
        return {
          card,
          name: card.dataset.name || '',
          address: card.dataset.address || '',
          status: card.dataset.status || 'open',
          url: card.dataset.url || '',
          cta: card.dataset.cta || '',
          cityState: card.dataset.cityState || '',
          address1: card.dataset.address1 || '',
          lat: Number.isFinite(lat) ? lat : null,
          lng: Number.isFinite(lng) ? lng : null,
          latlng: null,
          distance: null,
          marker: null,
          distanceEl: card.querySelector('[data-locator-distance]'),
          setButton: card.querySelector('[data-locator-set-location]'),
          currentBadge: card.querySelector('[data-locator-current-badge]')
        };
      });
    }

    loadLeaflet() {
      if (window.L) {
        this.initMap();
        return;
      }
      const stylesheet = new Promise((resolve) => {
        if (document.getElementById('leaflet-css')) {
          resolve();
          return;
        }
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = this.dataset.leafletCss;
        link.onload = resolve;
        link.onerror = resolve; // map still functions unstyled; don't block it
        document.head.appendChild(link);
      });
      const script = new Promise((resolve, reject) => {
        const existing = document.getElementById('leaflet-js');
        if (existing) {
          existing.addEventListener('load', resolve);
          existing.addEventListener('error', reject);
          return;
        }
        const el = document.createElement('script');
        el.id = 'leaflet-js';
        el.src = this.dataset.leafletSrc;
        el.async = true;
        el.onload = resolve;
        el.onerror = reject;
        document.body.appendChild(el);
      });
      Promise.all([stylesheet, script])
        .then(() => this.initMap())
        .catch(() => this.setStatus('The map failed to load — the location list below still works.'));
    }

    initMap() {
      this.map = L.map(this.mapEl, {
        scrollWheelZoom: false, // don't hijack page scroll on desktop
        dragging: !L.Browser.mobile // don't trap one-finger page scroll on touch; pinch-zoom still works
      });
      L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 19 }).addTo(this.map);

      const bounds = [];
      this.records.forEach((record) => {
        if (record.lat === null) return;
        record.latlng = [record.lat, record.lng];
        const isCurrent = record.address1 !== '' && record.address1 === this.currentFacilityKey;
        record.marker = L.marker(record.latlng, {
          icon: this.pinIcon(isCurrent),
          zIndexOffset: isCurrent ? 300 : 0,
          alt: record.name,
          title: record.name
        })
          .addTo(this.map)
          .bindPopup(this.popupHtml(record), {
            // auto-pan respects container edges only — pad the top-left so
            // popups clear the control column (zoom + recenter crosshair)
            autoPanPaddingTopLeft: L.point(64, 20),
            autoPanPaddingBottomRight: L.point(20, 20)
          })
          .on('click', () => this.selectCard(record, { scrollList: true }));
        bounds.push(record.latlng);
      });

      if (bounds.length) {
        this.map.fitBounds(L.latLngBounds(bounds).pad(0.1), { maxZoom: 15 });
      }

      // whole card pans the map — but never hijack its real links/buttons
      // (Book Now, Get directions, Set as my location)
      this.records.forEach((record) => {
        if (!record.marker) return;
        record.card.classList.add('_mappable');
        record.card.addEventListener('click', (event) => {
          if (event.target.closest('a, button')) return;
          this.showOnMap(record);
        });
      });

      if (this.recenterButton) {
        // keep clicks/drags on the control from reaching the map surface
        L.DomEvent.disableClickPropagation(this.recenterButton);
        this.recenterButton.hidden = false;
        this.recenterButton.addEventListener('click', this.onRecenter.bind(this));
      }

      // geolocation may have resolved while Leaflet was still loading
      this.updateOriginOnMap();
    }

    // A9 logo mark as the pin; the saved location also gets a "Your location"
    // pill. Anchored at the tail tip (~37% width).
    pinIcon(isCurrent) {
      const body = PIN_FOREST;
      const ball = '#ffffff';
      const ring = '#241f21';
      const html = `
        <svg viewBox="0 0 401 612" width="34" height="52" aria-hidden="true" focusable="false">
          <path fill="${ball}" d="m198.72 318.4c-31.24 0-64.29-13.02-86.27-33.99-22.6-22.59-36.01-56.5-36.01-90.83 0-34.32 13.08-67.07 35.87-89.86 22.68-21.64 56.07-35 87.28-35 31.83 0 64.39 13.04 87.13 34.89 22.87 21.93 35.99 53.79 35.99 87.39 0 33.46-13.37 67.32-35.76 90.58-22.48 23.4-54.64 36.82-88.23 36.82z"/>
          <path fill="${ring}" fill-rule="evenodd" d="m199.59 60.36c-33.27 0-68.87 14.24-92.91 37.17l-0.14 0.14-0.14 0.14c-24.35 24.35-38.32 59.25-38.32 95.77 0 36.5 14.33 72.61 38.32 96.6l0.14 0.15 0.14 0.13c23.83 22.73 58.24 36.3 92.04 36.3 35.85 0 70.21-14.36 94.26-39.39 23.84-24.78 38.09-60.81 38.09-96.37 0-35.87-14.06-69.92-38.57-93.43-24.24-23.3-58.97-37.21-92.91-37.21zm0 16.72c29.14 0 59.94 11.99 81.33 32.55 21.44 20.56 33.43 50.53 33.43 81.37 0 29.97-11.99 62.52-33.43 84.79-22.23 23.13-53.07 34.25-82.2 34.25-31.68 0-61.65-13.69-80.5-31.68-21.43-21.43-33.42-53.1-33.42-84.78 0-35.13 14.57-65.1 33.42-83.95 18.85-17.98 49.66-32.55 81.37-32.55z"/>
          <path fill="${body}" fill-rule="evenodd" d="m351.2 56.52c-23.98-24.84-69.42-56.52-147.34-56.52-63.35 0-111.34 21.39-147.34 58.23-34.22 35.13-56.52 85.65-56.52 143.05 0 59.1 23.14 101.06 52.27 130.19 23.14 23.11 65.94 53.11 133.61 53.11 10.28 0 19.69-0.87 30.84-3.45l-119.69 153.25 35.46 77.62 171.59-215.43c25.69-33.42 47.95-59.11 69.35-101.93 17.98-35.13 27.43-67.68 27.43-108.8 0-63.35-25.69-104.47-49.66-129.32zm-151.63 253.52c-31.67 0-61.64-13.7-80.49-31.68-21.44-21.43-33.43-53.11-33.43-84.78 0-35.13 14.57-65.1 33.43-83.95 18.85-17.98 49.65-32.55 81.37-32.55 29.13 0 59.93 11.99 81.33 32.55 21.43 20.56 33.42 50.53 33.42 81.37 0 29.97-11.99 62.52-33.42 84.79-22.23 23.13-53.07 34.25-82.21 34.25z M235.22 170.47c-7.19-9.17-20.5-10.77-29.67-3.58-1.27 0.99-2.01 2.41-2.19 3.89-0.18 1.48 0.21 3.03 1.2 4.29 1.99 2.53 5.64 2.97 8.18 0.98 4.12-3.22 10.09-2.5 13.32 1.61 3.22 4.11 2.51 10.1-1.61 13.32-2.53 1.99-2.97 5.65-0.99 8.18 1.99 2.53 5.65 2.98 8.19 0.99 9.16-7.2 10.77-20.51 3.57-29.68z M200.17 125.8c-7.2-9.17-20.51-10.77-29.68-3.57-1.27 0.99-2.01 2.4-2.19 3.88-0.18 1.48 0.21 3.03 1.2 4.3 1.99 2.52 5.65 2.97 8.18 0.98 4.12-3.23 10.09-2.51 13.32 1.6 3.13 3.99 2.39 10.19-1.6 13.32-2.53 1.99-2.98 5.65-0.99 8.18 1.99 2.53 5.65 2.98 8.18 0.99 9.16-7.2 10.77-20.51 3.58-29.68z M256.18 207.2c-5.61-0.67-11.13 0.87-15.58 4.36-1.26 0.99-2 2.41-2.18 3.88-0.18 1.49 0.2 3.03 1.2 4.3 1.99 2.53 5.65 2.97 8.18 0.98 1.99-1.56 4.47-2.25 6.99-1.95 2.51 0.3 4.76 1.57 6.32 3.56 3.13 3.99 2.39 10.19-1.6 13.32-2.53 1.99-2.97 5.65-0.98 8.18 1.98 2.53 5.64 2.97 8.17 0.98 4.44-3.48 7.26-8.49 7.94-14.09 0.67-5.61-0.87-11.14-4.36-15.58-3.48-4.44-8.49-7.26-14.1-7.94z M239.62 118.86c2-1.56 4.48-2.25 7-1.95 2.51 0.3 4.76 1.57 6.32 3.56 3.13 3.98 2.39 10.19-1.6 13.32-2.53 1.98-2.97 5.64-0.98 8.18 1.98 2.53 5.64 2.97 8.17 0.98 4.44-3.48 7.26-8.49 7.94-14.09 0.67-5.61-0.87-11.14-4.36-15.59-3.48-4.44-8.49-7.25-14.1-7.93-5.61-0.67-11.13 0.87-15.58 4.36-1.26 0.99-2.01 2.4-2.18 3.89-0.18 1.47 0.2 3.02 1.2 4.29 1.98 2.53 5.65 2.97 8.17 0.98z M297.17 157.94c-7.2-9.16-20.51-10.77-29.68-3.58-1.27 1-2.01 2.41-2.19 3.89-0.18 1.48 0.21 3.03 1.21 4.29 1.98 2.54 5.64 2.98 8.17 0.99 4.12-3.23 10.1-2.51 13.33 1.6 3.23 4.11 2.5 10.1-1.61 13.33-2.53 1.98-2.97 5.65-0.99 8.18 1.99 2.52 5.65 2.97 8.18 0.98 9.17-7.2 10.77-20.5 3.58-29.68z"/>
        </svg>${isCurrent ? '<span class="store-locator__pin-label">Your location</span>' : ''}`;
      return L.divIcon({
        className: `store-locator__pin${isCurrent ? ' store-locator__pin--current' : ''}`,
        html,
        iconSize: [34, 52],
        iconAnchor: [13, 52],
        popupAnchor: [4, -48]
      });
    }

    popupHtml(record) {
      const badge = record.status === 'open' ? 'Open 24/7' : 'Coming Soon';
      let html = `<strong class="store-locator__popup-name">${escapeHtml(record.name)}</strong>`
        + `<span class="store-locator__popup-badge">${badge}</span>`
        + `<span class="store-locator__popup-address">${escapeHtml(record.address)}</span>`;
      if (record.url && record.cta) {
        html += `<a class="store-locator__popup-link" href="${escapeHtml(record.url)}">${escapeHtml(record.cta)}</a>`;
      }
      return `<div class="store-locator__popup">${html}</div>`;
    }

    initSetLocationButtons() {
      // mirrors location-detector.js's storage convention ('selected_facility'
      // = the facility's address1); read directly because the detector instance
      // may not exist yet at custom-element upgrade time
      let currentFacility = null;
      try {
        currentFacility = localStorage.getItem('selected_facility');
      } catch (error) {
        currentFacility = null;
      }
      this.currentFacilityKey = currentFacility;
      this.records.forEach((record) => {
        if (!record.setButton || !record.cityState) return;
        if (currentFacility && record.address1 === currentFacility) {
          if (record.currentBadge) record.currentBadge.hidden = false;
          return;
        }
        record.setButton.hidden = false;
        record.setButton.addEventListener('click', () => this.onSetLocation(record));
      });
    }

    // Your saved facility keeps the first card slot; the distance sort flows
    // beneath it. Returns true when the order changed. _pinned is add-only:
    // safe while currentFacilityKey is fixed for the page's life ("Set as my
    // location" reloads) — revisit if the key ever becomes mutable in place.
    pinCurrentFacility() {
      if (!this.currentFacilityKey) return false;
      const index = this.records.findIndex(
        (record) => record.address1 !== '' && record.address1 === this.currentFacilityKey
      );
      if (index === -1) return false;
      this.records[index].card.classList.add('_pinned');
      if (index === 0) return false;
      this.records.unshift(...this.records.splice(index, 1));
      return true;
    }

    async onSetLocation(record) {
      const detector = window.locationDetector;
      if (!detector || typeof detector.saveLocation !== 'function') return;
      record.setButton.disabled = true;
      record.setButton.setAttribute('aria-busy', 'true');
      try {
        // navigates/reloads on completion, matching the header modal flow
        await detector.saveLocation(record.cityState, true, record.address1 || null);
      } catch (error) {
        console.error('Setting location failed:', error);
        record.setButton.disabled = false;
        record.setButton.removeAttribute('aria-busy');
      }
    }

    // Silent IP-based proximity sort on load (no permission prompt), reusing
    // the location detector's fetchIpLocation. Failure → server order stands.
    autoLocate() {
      const run = async () => {
        const detector = window.locationDetector;
        if (!detector || typeof detector.fetchIpLocation !== 'function') return;
        try {
          const ipLocation = await detector.fetchIpLocation();
          const lat = parseFloat(ipLocation.latitude);
          const lng = parseFloat(ipLocation.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
          if (this.hasExplicitOrigin) return;
          const zip = String(ipLocation.postal || '').trim();
          const input = this.searchForm && this.searchForm.querySelector('input[name="q"]');
          if (zip && input && !input.value) input.value = zip;
          this.applyOrigin(lat, lng, zip ? `your area (${zip})` : 'your area', { scrollToList: false });
        } catch (error) {
          /* IP lookup failed — keep server order */
        }
      };
      // the detector loads after this script, so poll briefly for its instance
      let attempts = 0;
      const waitForDetector = () => {
        if (window.locationDetector) {
          run();
          return;
        }
        attempts += 1;
        if (attempts > 20) return; // ~3s — detector genuinely absent
        setTimeout(waitForDetector, 150);
      };
      waitForDetector();
    }

    // Collapse the list past the limit; the map always shows every pin.
    applyListLimit() {
      const collapsible = this.listLimit > 0 && this.records.length > this.listLimit;
      this.records.forEach((record, index) => {
        record.card.classList.toggle(
          '_collapsed',
          collapsible && !this.expanded && index >= this.listLimit
        );
      });
      if (!this.toggleButton) return;
      this.toggleButton.hidden = !collapsible;
      if (collapsible) {
        this.toggleButton.textContent = this.expanded
          ? 'Show fewer locations'
          : `Show all ${this.records.length} locations`;
        this.toggleButton.setAttribute('aria-expanded', String(this.expanded));
      }
    }

    onToggleList() {
      this.expanded = !this.expanded;
      this.applyListLimit();
      if (!this.expanded) {
        this.listEl.scrollIntoView({ block: 'nearest', behavior: SCROLL_BEHAVIOR });
      }
    }

    selectCard(record, { scrollList }) {
      // a pin click can target a collapsed card — reveal the list first
      if (record.card.classList.contains('_collapsed')) {
        this.expanded = true;
        this.applyListLimit();
      }
      this.records.forEach((other) => {
        const selected = other === record;
        other.card.classList.toggle('_selected', selected);
        if (other.marker && other.marker.getElement) {
          const markerEl = other.marker.getElement();
          if (markerEl) markerEl.classList.toggle('_selected', selected);
        }
      });
      if (scrollList) {
        record.card.scrollIntoView({ block: 'nearest', behavior: SCROLL_BEHAVIOR });
      }
    }

    showOnMap(record) {
      this.selectCard(record, { scrollList: false });
      if (!this.map || !record.marker) return;
      this.map.setView(record.latlng, Math.max(this.map.getZoom(), 12));
      record.marker.openPopup();
      this.mapEl.scrollIntoView({ block: 'nearest', behavior: SCROLL_BEHAVIOR });
    }

    onGeolocate() {
      this.hasExplicitOrigin = true;
      this.geoButton.disabled = true;
      this.geoButton.setAttribute('aria-busy', 'true');
      const done = () => {
        this.geoButton.disabled = false;
        this.geoButton.removeAttribute('aria-busy');
      };
      navigator.geolocation.getCurrentPosition(
        (position) => {
          done();
          this.applyOrigin(position.coords.latitude, position.coords.longitude, 'your location');
        },
        () => {
          done();
          this.setStatus("We couldn't access your location — try searching by city or ZIP instead.");
        },
        { timeout: 10000, maximumAge: 300000 }
      );
    }

    // Crosshair control: take the map back to the visitor. Recenter-only by
    // design — with a known origin it never re-sorts, prompts, or scrolls;
    // the toolbar "Use my location" button owns find-me-and-sort.
    onRecenter() {
      if (this.lastOrigin) {
        this.updateOriginOnMap();
        return;
      }
      if (!navigator.geolocation || !window.isSecureContext) {
        this.setStatus("We couldn't access your location — try searching by city or ZIP instead.");
        return;
      }
      this.hasExplicitOrigin = true;
      this.recenterButton.disabled = true;
      this.recenterButton.setAttribute('aria-busy', 'true');
      const done = () => {
        this.recenterButton.disabled = false;
        this.recenterButton.removeAttribute('aria-busy');
      };
      navigator.geolocation.getCurrentPosition(
        (position) => {
          done();
          // a map control acts on the map — never scroll the page to the list
          this.applyOrigin(position.coords.latitude, position.coords.longitude, 'your location', { scrollToList: false });
        },
        () => {
          done();
          this.setStatus("We couldn't access your location — try searching by city or ZIP instead.");
        },
        { timeout: 10000, maximumAge: 300000 }
      );
    }

    async onSearch(event) {
      event.preventDefault();
      const submit = this.searchForm.querySelector('[data-locator-submit]');
      if (submit.disabled) return; // request already in flight
      const input = this.searchForm.querySelector('input[name="q"]');
      const query = (input.value || '').trim();
      if (!query) return;
      this.hasExplicitOrigin = true;

      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      this.setStatus(`Searching for “${query}”…`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), SEARCH_TIMEOUT_MS);
      try {
        const zip = query.match(/^(\d{5})(-\d{4})?$/);
        const params = zip ? `postalcode=${zip[1]}` : `q=${encodeURIComponent(query)}`;
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=us&${params}`,
          { headers: { Accept: 'application/json' }, signal: controller.signal }
        );
        if (!response.ok) throw new Error(`Geocoding failed (${response.status})`);
        const results = await response.json();
        if (!results.length) {
          this.setStatus(`We couldn't find “${query}” — try a city and state, or a 5-digit ZIP.`);
          return;
        }
        this.applyOrigin(parseFloat(results[0].lat), parseFloat(results[0].lon), `“${query}”`);
      } catch (error) {
        console.error('Store locator search failed:', error);
        this.setStatus('Search is unavailable right now — please try again in a moment.');
      } finally {
        clearTimeout(timeoutId);
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      }
    }

    // scrollToList: true for explicit search/geolocate; false for the silent
    // on-load sort (yanking a just-arrived visitor down the page feels broken)
    applyOrigin(lat, lng, label, { scrollToList = true } = {}) {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      this.records.forEach((record) => {
        record.distance = record.lat === null ? null : distanceInMiles(lat, lng, record.lat, record.lng);
      });
      // stable sort: located nearest-first, unlocated keep server order at the end
      this.records.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));

      // capture the true nearest before the pinned card jumps the queue —
      // the status line and highlight must stay honest
      const located = this.records.filter((record) => record.distance !== null);
      const nearest = located[0] || null;
      this.pinCurrentFacility();

      this.listEl.classList.add('_sorted'); // stop the first-paint stagger replaying
      this.records.forEach((record) => {
        this.listEl.appendChild(record.card);
        if (!record.distanceEl) return;
        if (record.distance === null) {
          record.distanceEl.hidden = true;
          return;
        }
        record.distanceEl.textContent = `${formatMiles(record.distance)} mi away`;
        record.distanceEl.hidden = false;
      });
      this.applyListLimit();

      if (nearest) {
        this.setStatus(
          `${located.length} locations sorted by distance from ${label}. `
          + `Nearest: ${nearest.name} (${formatMiles(nearest.distance)} mi).`
        );
        if (scrollToList) {
          this.listEl.scrollIntoView({ block: 'nearest', behavior: SCROLL_BEHAVIOR });
        }
      }

      this.lastOrigin = [lat, lng];
      this.updateOriginOnMap();
    }

    // Draw/move the origin dot and frame origin + nearest suite. Callable
    // from applyOrigin AND after a late map init (geolocation on load usually
    // resolves before Leaflet has been lazy-loaded).
    updateOriginOnMap() {
      if (!this.map || !this.lastOrigin) return;
      if (this.originMarker) {
        this.originMarker.setLatLng(this.lastOrigin);
      } else {
        // divIcon (not circleMarker) so CSS can animate the radiating ring
        this.originMarker = L.marker(this.lastOrigin, {
          icon: L.divIcon({
            className: 'store-locator__origin-icon',
            html: '<span class="store-locator__origin"></span>',
            iconSize: [14, 14],
            iconAnchor: [7, 7]
          }),
          interactive: false,
          keyboard: false,
          zIndexOffset: 500
        }).addTo(this.map);
      }
      // records may be pinned-first — frame the true nearest, not records[0]
      let nearest = null;
      this.records.forEach((record) => {
        if (record.distance === null || !record.latlng) return;
        if (!nearest || record.distance < nearest.distance) nearest = record;
      });
      if (nearest) {
        this.map.fitBounds(L.latLngBounds([this.lastOrigin, nearest.latlng]).pad(0.4), { maxZoom: 12 });
      } else {
        this.map.setView(this.lastOrigin, 9);
      }
    }

    setStatus(message) {
      if (this.statusEl) this.statusEl.textContent = message;
    }
  }

  customElements.define('store-locator', StoreLocator);
})();
